import {
  AuthError,
  BrowserAuthErrorCodes,
  CacheLookupPolicy,
  ClientAuthErrorCodes,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from '@azure/msal-browser';

import { decodeState, encodeState, isSitePath, setPostAuthIntent } from './postAuth';

import type { AuthApi } from './types';
import type { SiteEnv } from '../env';
import type {
  AccountInfo,
  AuthenticationResult,
  Configuration,
  IPublicClientApplication,
  RedirectRequest,
} from '@azure/msal-browser';

/** The Direwolf AI account tenant (Microsoft Entra External ID) as this site uses it. */
export interface EntraConfig {
  /** Directory (tenant) id; tokens are issued by `https://<tenantId>.ciamlogin.com/<tenantId>/v2.0`. */
  tenantId: string;
  /** The account site's app registration. */
  clientId: string;
  /** `login.direwolfai.com` — the custom URL domain that serves the sign-in pages. */
  authorityHost: string;
  /** `direwolfai` — `direwolfai.ciamlogin.com`. */
  tenantSubdomain: string;
  /** `api://account.direwolfai.com/<apiAppId>/Account.Manage`. */
  apiScope: string;
}

export function entraConfigFromEnv(env: SiteEnv): EntraConfig | null {
  const {
    VITE_ENTRA_TENANT_ID: tenantId,
    VITE_ENTRA_CLIENT_ID: clientId,
    VITE_ENTRA_AUTHORITY_HOST: authorityHost,
    VITE_ENTRA_TENANT_SUBDOMAIN: tenantSubdomain,
    VITE_ACCOUNT_API_SCOPE: apiScope,
  } = env;
  if (!tenantId || !clientId || !authorityHost || !tenantSubdomain || !apiScope) return null;
  return { tenantId, clientId, authorityHost, tenantSubdomain, apiScope };
}

/** Requested at every sign-in: the OpenID Connect scopes and the account API's scope. */
export function signInScopes(config: EntraConfig): string[] {
  return ['openid', 'profile', 'offline_access', config.apiScope];
}

/**
 * Redirect flow only, tokens in session storage (gone with the tab), callback on `/auth/callback`,
 * signed-out page `/signed-out`.
 *
 * Hosts: the sign-in pages are served on the custom URL domain (`https://login.direwolfai.com/<tenantId>/`),
 * but the tenant's OIDC `issuer` lives on `https://<tenantId>.ciamlogin.com/`. MSAL ≥ 5 validates
 * the discovery issuer's host against the authority and `knownAuthorities`, so every host the
 * tenant answers on is listed — otherwise `loginRedirect` fails before navigating, with
 * `endpoints_resolution_error`.
 */
export function msalConfiguration(config: EntraConfig, origin: string): Configuration {
  return {
    auth: {
      clientId: config.clientId,
      authority: `https://${config.authorityHost}/${config.tenantId}/`,
      knownAuthorities: [
        config.authorityHost,
        `${config.tenantSubdomain}.ciamlogin.com`,
        `${config.tenantId}.ciamlogin.com`,
      ],
      redirectUri: `${origin}/auth/callback`,
      postLogoutRedirectUri: `${origin}/signed-out`,
    },
    cache: { cacheLocation: 'sessionStorage' },
  };
}

/**
 * Creates the MSAL instance and finishes a redirect that has just returned (/auth/callback). Runs
 * before the router decides where to go, which then reads the redirect's intent (postAuth.ts).
 */
export async function createMsalInstance(config: EntraConfig): Promise<PublicClientApplication> {
  const pca = new PublicClientApplication(msalConfiguration(config, window.location.origin));
  await pca.initialize();
  let result: AuthenticationResult | null = null;
  try {
    // The router owns navigation after sign-in (/auth/callback), not MSAL.
    result = await pca.handleRedirectPromise({ navigateToLoginRequestUrl: false });
  } catch {
    // The sign-in or sign-up did not complete (cancelled, or refused): carry on signed out.
  }
  const account = result?.account ?? pca.getActiveAccount() ?? pca.getAllAccounts()[0] ?? null;
  if (account) pca.setActiveAccount(account);
  setPostAuthIntent(decodeState(result?.state));
  return pca;
}

/** The part of MSAL the site calls (tests pass a double of just this). */
export type MsalClient = Pick<
  IPublicClientApplication,
  'acquireTokenSilent' | 'acquireTokenRedirect' | 'loginRedirect' | 'logoutRedirect' | 'clearCache'
>;

/**
 * Failures, besides any `InteractionRequiredAuthError`, that only a new sign-in resolves: a refresh
 * token the server refused (`invalid_grant`), a cache that cannot serve the request without a
 * renewal, and a renewal that timed out.
 */
const SIGN_IN_REQUIRED_CODES: ReadonlySet<string> = new Set([
  'invalid_grant',
  ClientAuthErrorCodes.tokenRefreshRequired,
  BrowserAuthErrorCodes.timedOut,
]);

/** Whether a failed silent token request means "sign in again" rather than a passing failure. */
export function isSignInRequired(error: unknown): boolean {
  if (error instanceof InteractionRequiredAuthError) return true;
  return error instanceof AuthError && SIGN_IN_REQUIRED_CODES.has(error.errorCode);
}

/** One sign-in redirect per MSAL instance at a time: concurrent token requests share it. */
const pendingRedirects = new WeakMap<MsalClient, Promise<void>>();

function redirectForToken(instance: MsalClient, request: RedirectRequest): Promise<void> {
  let pending = pendingRedirects.get(instance);
  if (!pending) {
    pending = instance
      .acquireTokenRedirect(request)
      .finally(() => pendingRedirects.delete(instance));
    pendingRedirects.set(instance, pending);
  }
  return pending;
}

/** The page the person is on; a sign-in started from it returns here. */
function currentPath(): string {
  const { pathname, search } = window.location;
  return `${pathname}${search}`;
}

/**
 * The site's `AuthApi` over an MSAL instance.
 *
 * Tokens come from the cache or a refresh-token exchange only
 * (`CacheLookupPolicy.AccessTokenAndRefreshToken`), never from MSAL's hidden-iframe renewal: the
 * site refuses to be framed (`frame-ancestors 'none'`, `frame-src 'none'`), so that attempt could
 * only time out. When the refresh token is missing, expired or refused, the person is sent to sign
 * in and brought back to the same page.
 */
export function createMsalAuthApi(
  instance: MsalClient,
  account: AccountInfo | null,
  config: EntraConfig,
): AuthApi {
  const scopes = signInScopes(config);
  const apiScopes = [config.apiScope];
  return {
    status: account ? 'authenticated' : 'anonymous',
    account: account
      ? {
          id: account.homeAccountId,
          name: account.name ?? null,
          email: account.username === '' ? null : account.username,
        }
      : null,
    signIn: (returnTo = '/') =>
      instance.loginRedirect({
        scopes,
        state: encodeState({ kind: 'path', path: isSitePath(returnTo) ? returnTo : '/' }),
      }),
    createAccount: (returnOrigin) =>
      instance.loginRedirect({
        scopes,
        prompt: 'create',
        state: encodeState({ kind: 'created', returnOrigin }),
      }),
    reauthenticate: (returnTo) =>
      instance.loginRedirect({
        scopes,
        prompt: 'login',
        ...(account?.username ? { loginHint: account.username } : {}),
        state: encodeState({ kind: 'path', path: returnTo }),
      }),
    signOut: () => instance.logoutRedirect(account ? { account } : undefined),
    forgetSession: () => instance.clearCache(account ? { account } : undefined),
    getAccessToken: async () => {
      if (!account) return null;
      try {
        const result = await instance.acquireTokenSilent({
          scopes: apiScopes,
          account,
          cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken,
        });
        return result.accessToken;
      } catch (error) {
        // Offline, or the sign-in service is briefly unavailable: no token this time.
        if (!isSignInRequired(error)) return null;
        try {
          await redirectForToken(instance, {
            scopes: apiScopes,
            account,
            state: encodeState({ kind: 'path', path: currentPath() }),
          });
        } catch {
          // The redirect could not start (the connection dropped): no token this time.
        }
        return null;
      }
    },
  };
}
